import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { ConversationBuilderLocale } from "@/locales/localeid";
import { ConversationBuilderState } from "@/modules/conversation-builder";
import { InteractionStyleModel } from "@/services/conversation-builder";
import { InteractionStyles } from "@/util/enum";
import { getActualFileNameAndType } from "@/util/func";
import {
	Button,
	Checkbox,
	Empty,
	Form,
	Icon,
	Input,
	InputNumber,
	List,
	Modal,
	Progress,
	Select,
	Upload
} from "ant-design-vue";
import { WrappedFormUtils } from "ant-design-vue/types/form/form";
import { ActionItem, fmtMsg } from "commonui";
import { debounce, has } from "lodash";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { EntityLengths } from "../../conversation-builder-util";
import {
	ConversationNode,
	ConversationNodeType,
	EntityDataSetDirectory,
	Path,
	PathIntent
} from "../../conversation-builder.model";
import { Entity } from "../../models/entity.model";
import { Intent } from "../../models/intent.model";
import IConversationImageEditor from "../conversation-image-editor/conversation-image-editor";
import ConversationImageEditor from "../conversation-image-editor/conversation-image-editor.vue";
import { IIntentAddDialog, IntentAddDialog } from "../intent-add-dialog";
import { IIntentManageDrawer, IntentManageDrawer } from "../intent-manage-drawer";

@Component({
	components: {
		Form,
		FormItem: Form.Item,
		InputNumber,
		Input,
		TextArea: Input.TextArea,
		Upload,
		Button,
		Icon,
		Checkbox,
		List,
		ListItem: List.Item,
		Empty,
		ActionItem,
		ConversationImageEditor,
		Modal,
		Progress,
		Grid,
		IntentAddDialog,
		IntentManageDrawer,
		Select,
		Option: Select.Option
	}
})
export default class IntentProperties extends Vue {
	@Prop() node!: ConversationNode<Path>;
	public form!: WrappedFormUtils;
	private emitEvent = true;
	public maxIntentLength = EntityLengths.path.name.max;
	public debounce = debounce;

	public conversationStore = getModule(ConversationBuilderState);
	public selectedIntent: Intent | null = null;

	$refs!: {
		imageEditor: IConversationImageEditor;
		intentAddDialogRef: IIntentAddDialog;
		// intentmanageDrawerRef: IIntentManageDrawer;
	};

	created() {
		this.form = this.$form.createForm(this, {
			name: "dialog-properties",
			onValuesChange: debounce(this.onValuesChange, 500)
		});
	}

	mounted() {
		this.setDefaultValues();
	}

	@Watch("conversationStore.selectedNode")
	onChange() {
		this.setDefaultValues();
	}

	public get path(): ConversationNode<Path> {
		return this.conversationStore.nodes[this.node.id] as ConversationNode<Path>;
	}

	public get isFallback() {
		return this.node.data?.isFallback;
	}

	public get imageUrl() {
		return this.node.data.imageUrl as string;
	}

	public get getImageName() {
		if (this.imageUrl) {
			return getActualFileNameAndType(this.imageUrl);
		}
	}

	public get intents(): Intent[] {
		return this.node.data.intents.map((pathIntent) => this.conversationStore.intents.directory[pathIntent.id]);
	}

	public get interactionStyles(): InteractionStyleModel[] {
		return this.conversationStore.interactionStyles
			.filter((s) => {
				if (
					s.id === InteractionStyles.Positive ||
					s.id === InteractionStyles.Negative ||
					s.id === InteractionStyles.Encouraging
				) {
					return true;
				}
				return false;
			})
			.map((s) => s);
	}

	public addIntent() {
		this.manageIntentClose();
		this.$refs.intentAddDialogRef.open();
	}

	public manageIntent(intent: Intent) {
		this.selectedIntent = intent;
	}

	public manageIntentClose() {
		if (this.selectedIntent) {
			const el = document.getElementById("intent" + this.selectedIntent.id);
			if (el) {
				el.focus();
			}
		}
		this.selectedIntent = null;
	}

	public async downloadImage() {
		this.conversationStore.downloadFile(this.imageUrl);
	}

	public removeImage(closeEditor: () => void) {
		const remove = async () => {
			closeEditor && closeEditor();
			await this.conversationStore.deleteImage(this.node);
		};

		Modal.confirm({
			title: fmtMsg(ConversationBuilderLocale.ConversationDialogImageRemoveMsg),
			onOk: remove
		});
	}

	isImageUploadInProgress() {
		return this.conversationStore.fileUploads.find((f) => f.nodeId === this.node.id);
	}

	get imageUploadProgress() {
		const uploadInfo = this.isImageUploadInProgress();
		if (uploadInfo) {
			return Math.floor((uploadInfo.uploaded * 100) / uploadInfo.size);
		}
	}

	private setDefaultValues() {
		this.setValues(
			{
				name: this.node.data.name,
				isFallback: this.node.data.isFallback,
				interactionStyleId: this.node.data.interactionStyleId
			},
			false
		);
	}

	private setValues(values: Partial<Path>, emitEvent = true) {
		this.emitEvent = emitEvent;
		this.form.setFieldsValue(values);
	}

	private onValuesChange(props: any, fields: Partial<Path>) {
		if (!this.emitEvent) {
			this.emitEvent = true;
			return null;
		}

		const saveChanges = () => {
			if (typeof fields.isFallback === "boolean") {
				if (fields.isFallback) {
					fields.name = fmtMsg(ConversationBuilderLocale.ConversationPathDefaultFallbackName);
					this.setValues(
						{ name: fmtMsg(ConversationBuilderLocale.ConversationPathDefaultFallbackName) },
						false
					);
				} else {
					fields.name = fmtMsg(ConversationBuilderLocale.ConversationPathDefaultName);
					this.setValues({ name: fmtMsg(ConversationBuilderLocale.ConversationPathDefaultName) }, false);
				}
			}

			this.conversationStore.updateNodeData<Path>({
				id: this.node.id,
				data: {
					...fields
				}
			});
		};

		// Showing a confirmation dialog when isFallback is checked and the path has intent associated.
		if (has(fields, "isFallback") && fields.isFallback && !!this.node.data.intents.length) {
			Modal.confirm({
				title: "Confirmation",
				content:
					"This path has intent(s) associated with which will be deleted. Are you sure you want to continue with the change?",
				onOk: () => {
					this.node.data.intents.forEach((intent) => {
						this.onIntentRemove(this.conversationStore.intents.directory[intent.id], true);
					});
					saveChanges();
				},
				onCancel: () => {
					this.setValues({ isFallback: false }, false);
				}
			});
		} else {
			saveChanges();
		}
	}

	public onIntentAdd(intent: Intent) {
		// checking whether the intent is already attached to current path
		const existingIntent = this.node.data.intents.find((i) => i.id === intent.id);
		if (existingIntent) {
			return;
		}

		// proceed with adding intent to current path
		const intentToAdd: PathIntent = {
			id: intent.id
		};
		this.conversationStore.updateNodeData<Path>({
			id: this.node.id,
			data: { ...this.node.data, intents: [...this.node.data.intents, intentToAdd] }
		});

		this.manageIntent(intent);
	}

	public onIntentRemove(intent: Intent, doNotShowAlert?: boolean, doNotRemoveEntity?: boolean) {
		const removeIntentReference = () => {
			const intents = this.path.data.intents.filter((i) => i.id !== intent.id);
			let entityValueIds: string[] = [];

			// If intent used an entity. get all entity's value ids. and filter path's entity value ids.
			if (intent.entityId && this.conversationStore.entities.directory[intent.entityId]) {
				entityValueIds = this.conversationStore.entities.directory[intent.entityId].values.map((v) => v.id);
			}

			this.conversationStore.updateNodeData<Path>({
				id: this.node.id,
				data: {
					...this.node.data,
					intents,
					entityValueIds: this.node.data.entityValueIds.filter((evId) => !entityValueIds.includes(evId))
				}
			});

			this.selectedIntent = null;
		};

		const deleteIntent = () => {
			this.conversationStore.removeIntent(intent.id);
			if (intent.entityId && !doNotRemoveEntity) {
				this.conversationStore.removeEntityIfNoReferenceExists(intent.entityId);
			}
		};

		// Remove intent if no references exists with showing a confermation message
		let isReferenced = false;
		for (const key in this.conversationStore.nodes) {
			const node = this.conversationStore.nodes[key];
			const isNotCurrentPath = node.id !== this.node.id;
			if (node.type === ConversationNodeType.Path && isNotCurrentPath) {
				const nodeData = node.data as Path;
				const isIntentExists = !!nodeData.intents.find((ref) => ref.id === intent.id);
				if (isIntentExists) {
					isReferenced = true;
				}
			}
		}

		if (isReferenced) {
			removeIntentReference();
		} else if (intent.commonIntentId || doNotShowAlert) {
			/**
			 * We don't need to show confirmation message as the common intent can be imported again, won't be lost permanently.
			 * And if doNotShowAlert is set to true
			 */
			removeIntentReference();
			deleteIntent();
		} else {
			// We need to show confirmation message as the intent cannot be recovered, would be deleted permanently.
			Modal.confirm({
				title: "Confirmation",
				content:
					"The intent and the linked entity would be deleted permanently. Are you sure, you want to continue with the change.",
				onOk: () => {
					removeIntentReference();
					deleteIntent();
				}
			});
		}
	}

	updateIntent(newIntent: Intent) {
		this.selectedIntent = newIntent;
	}

	public openImageEditor() {
		this.$refs.imageEditor.startEditing();
	}
}
